// Bloques de "Quién va al evento" compartidos por la pestaña Logística
// (QuienVaEvento.js, variante "l1", tal cual se veía) y Logística 2
// (variante "l2", con sus retoques):
// - ResponsableMontaje: equipo propio o un extra, persona, recogida más tarde.
// - HorarioTramos: jornada del responsable, llegada a la nave y fin de la
//   descarga y la tabla de tramos editables. En l2: sin la línea del día,
//   nombres en lenguaje llano, horas redondeadas a 5 min, el solape de montaje
//   intercalado entre los tramos y el segundo viaje en la misma tabla.
// - PersonalMenu: personal del menú según ratios. En l2: solo los menús que
//   llevan camareros o extras, con "Ver todos los menús" para el resto. Cada
//   fila enseña "Propuesto" (lo que calcula el ERP) y "Van" con - y + (y el
//   número a mano), extras y horas igual, "Volver a lo propuesto" y "Horas
//   distintas": tandas de camareros con su hora de entrada y de salida
//   (20-oct-2026, petición de Alfonso). Arriba, "Guardando..." / "Guardado".
// No tienen estado de negocio: todo llega en `v` (el estado de QuienVaEvento o
// el del hook useQuienVaEvento, con los mismos nombres).
// Multievento con una jornada elegida (Logística 2): `v` es el de esa jornada
// y `v.ambitoJornada` dice qué es propio de la jornada y qué hereda del evento.
import React, { useEffect, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import {
  MAX_TANDAS,
  QUIEN_RECOGE,
  TRAMOS_QUIEN_VA,
  formatoMinutos,
  horaConDia,
  horaTexto,
  horasPersonaFila,
  lineaDelDia,
  minutosDeHora,
  minutosTanda,
  normalizarHoraEscrita,
  textoCantidadFila,
} from '../../../utils/quienVaEvento';
import { duracionRedondeada, horaRedondeadaConDia, textoSolape } from '../../../utils/logistica2';
import { CLASE_ERROR_FRANJA, errorFranja } from '../../../utils/horasServicio';
import { revisarNovedades } from '../../guia/novedades';

const formatEuros = (n) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);
const formatHoras = (h) => (Number(h) || 0).toLocaleString('es-ES', { maximumFractionDigits: 2 });
const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;
const r2 = (n) => Math.round(((Number(n) || 0) + Number.EPSILON) * 100) / 100;

const claseBoton = 'h-[34px] px-3 rounded-lg border text-[13px] font-bold whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed';
const botonPrincipal = `${claseBoton} bg-duetto-primary border-duetto-primary text-white hover:bg-opacity-90`;
const botonSecundario = `${claseBoton} bg-white border-[#CBD3CF] text-[#16221D] hover:bg-gray-50`;
const claseInput = 'px-2 py-1.5 min-h-[44px] md:min-h-0 text-base md:text-sm text-right border border-[#CBD3CF] rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary tabular-nums';
const claseTitulo = 'text-sm font-extrabold text-[#16221D]';

const fechaCorta = (iso) => (iso ? `${String(iso).slice(8, 10)}/${String(iso).slice(5, 7)}/${String(iso).slice(0, 4)}` : '');
const fechaDiaMes = (iso) => (iso ? `${String(iso).slice(8, 10)}/${String(iso).slice(5, 7)}` : '');
const limpiar = (v) => { if (v.limpiarAviso) v.limpiarAviso(); };

// ─────────────────────────────────────────────────────────────────────────────
// a) Responsable del montaje
// ─────────────────────────────────────────────────────────────────────────────

// Valor del selector de persona para "la misma persona que en el evento" (jornada)
export const PERSONA_DEL_EVENTO = '__evento__';

export const ResponsableMontaje = ({ v }) => {
  const {
    multi, jornadas, raiz, calculo, tarifas, modo, sinCostePersona, personaId, personaNombre, empleados,
    guardarPersona, guardandoPersona, avisoPersona, onAbrirSolicitud, hayOrdenExtra, personasOrdenExtra,
    externosCalculo, slotPropio, slotExtra, seVa, cambiarPermanencia, tipoRecogida, cambiarTipoRecogida,
    fechaRecogida, setFechaRecogida, horaRecogida, setHoraRecogida, quienRecoge, setQuienRecoge,
    minutosRecogida, setMinutosRecogida, presupuesto, recogida, lineaRecogida, regalarRecogida, regalando,
    onAbrirPersonal, numPersonalAsignado, ambitoJornada = null,
  } = v;
  const elegirModo = v.elegirModo || v.setModo;
  const aj = ambitoJornada;
  const etiquetaModo = (m) => {
    if (m === 'extra') return 'un extra';
    if (m === 'propio') return 'equipo propio';
    return 'sin elegir';
  };
  const textoDesgloseExternos = [
    externosCalculo.camareros > 0 ? plural(externosCalculo.camareros, 'camarero', 'camareros') : null,
    externosCalculo.extras > 0 ? plural(externosCalculo.extras, 'extra', 'extras') : null,
  ].filter(Boolean).join(' + ');
  const quienRecogeTexto = (QUIEN_RECOGE.find(q => q.clave === (recogida?.quien)) || {}).etiqueta || '';
  const sugerencias = multi
    ? jornadas.filter(x => x.est.sugerenciaRecogida).map(x => `${x.j.etiqueta}: ${x.est.sugerenciaRecogida}`)
    : (raiz.sugerenciaRecogida ? [raiz.sugerenciaRecogida] : []);

  const textoDiferencia = (() => {
    if (calculo.masBarato === 'igual') return 'Cuesta lo mismo con el equipo propio que con un extra.';
    const importe = formatEuros(Math.abs(calculo.diferencia));
    return calculo.masBarato === 'propio'
      ? `Con un extra cuesta ${importe} más que con el equipo propio.`
      : `Con un extra cuesta ${importe} menos que con el equipo propio.`;
  })();

  const tarjetaOpcion = (clave, titulo, coste, tarifa) => {
    const elegido = modo === clave;
    return (
      <button
        type="button"
        onClick={() => { elegirModo(clave); }}
        aria-pressed={elegido}
        className={`flex-1 min-w-[150px] text-left rounded-xl border-2 px-3.5 py-3 transition-colors ${
          elegido ? 'border-duetto-primary bg-[#E3F3EC]' : 'border-[#E1E6E3] bg-white hover:border-[#9FC7B6]'
        }`}
        data-testid={`quien-va-opcion-${clave}`}
      >
        <span className="block text-sm font-bold text-[#16221D]">{titulo}</span>
        <span className="block text-xl font-extrabold tabular-nums text-[#16221D] mt-1">{formatEuros(coste)}</span>
        <span className="block text-xs text-[#5B6B64]" data-testid={`quien-va-opcion-${clave}-detalle`}>
          {clave === 'propio' && sinCostePersona && !calculo.recogida?.aparte && `${formatHoras(calculo.horas)} h · Sin coste: socio`}
          {clave === 'propio' && sinCostePersona && calculo.recogida?.aparte
            && `Montaje sin coste (socio) + recogida (${(QUIEN_RECOGE.find(q => q.clave === calculo.recogida.quien) || {}).etiqueta || ''})`}
          {!(clave === 'propio' && sinCostePersona) && (calculo.recogida?.aparte
            ? `Montaje a ${formatEuros(tarifa)}/h + recogida (${(QUIEN_RECOGE.find(q => q.clave === calculo.recogida.quien) || {}).etiqueta || ''})`
            : `${formatHoras(calculo.horas)} h × ${formatEuros(tarifa)}/h`)}
        </span>
      </button>
    );
  };

  return (
    <section className="flex flex-col gap-3 min-w-0" data-testid="quien-va-responsable">
      <h3 className={claseTitulo}>
        {aj ? `Responsable del montaje en la ${aj.etiqueta} (1 persona)` : `Responsable del montaje (1 persona${multi ? ' por jornada' : ''})`}
      </h3>
      {aj && (
        <p className="text-xs text-[#5B6B64] flex flex-wrap items-center gap-2" data-testid="quien-va-jornada-modo">
          {aj.modoPropio
            ? `Esta jornada tiene su propio responsable. En el evento: ${etiquetaModo(aj.modoEvento)}.`
            : `Igual que el evento (${etiquetaModo(aj.modoEvento)}). Elige otra opción para cambiarlo solo en esta jornada.`}
          {aj.modoPropio && aj.onHeredarModo && (
            <button type="button" onClick={aj.onHeredarModo}
              className="font-semibold text-[#11704F] hover:underline" data-testid="quien-va-jornada-heredar-modo">
              Usar lo del evento
            </button>
          )}
        </p>
      )}
      <div className="flex flex-wrap gap-3">
        {tarjetaOpcion('propio', 'Equipo propio', calculo.costePropio, tarifas.propio)}
        {tarjetaOpcion('extra', 'Un extra', calculo.costeExtra, tarifas.extra)}
      </div>
      <p className="text-xs text-[#5B6B64]" data-testid="quien-va-ayuda-modo">
        El equipo propio monta el evento; los camareros y extras del menú se piden aparte en la orden de personal extra.
      </p>
      <p className="text-sm font-semibold text-[#16221D]" data-testid="quien-va-diferencia">
        Total {formatoMinutos(calculo.totalMinutos)} ({formatHoras(calculo.horas)} h{multi ? `, ${jornadas.length} jornadas` : ''}). {textoDiferencia}
      </p>

      {/* Equipo propio: quién va; extra: la orden de personal extra ahí mismo */}
      {modo === 'propio' && (
        <div className="rounded-lg border border-[#E1E6E3] px-3 py-2.5 flex flex-col gap-2" data-testid="quien-va-persona">
          <label className="flex flex-wrap items-center gap-2 text-sm font-semibold text-[#16221D]">
            Persona del equipo propio que va
            <select
              value={aj && !aj.personaPropia ? PERSONA_DEL_EVENTO : personaId}
              onChange={(e) => guardarPersona(e.target.value)}
              disabled={guardandoPersona || empleados === null}
              className="px-2 py-1.5 text-sm font-normal border border-[#CBD3CF] rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary bg-white disabled:opacity-60"
              data-testid="quien-va-persona-select"
            >
              {aj && (
                <option value={PERSONA_DEL_EVENTO}>
                  {`Como en el evento${aj.personaEventoNombre ? ` (${aj.personaEventoNombre})` : ' (sin elegir)'}`}
                </option>
              )}
              <option value="">{empleados === null ? 'Cargando...' : 'Sin elegir'}</option>
              {(empleados || []).map(e => (
                <option key={e.id} value={e.id}>{e.nombre}</option>
              ))}
              {personaId && empleados !== null && !empleados.some(e => e.id === personaId) && (
                <option value={personaId}>{personaNombre || 'Persona guardada'} (no está activa)</option>
              )}
            </select>
            {personaId && (
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-bold ${sinCostePersona ? 'bg-[#E3F3EC] text-[#0F5E43]' : 'bg-[#EEF1EF] text-[#4A5A53]'}`}
                data-testid="quien-va-persona-coste"
              >
                {sinCostePersona ? 'Sin coste (socio)' : `${formatEuros(tarifas.propio)}/h`}
              </span>
            )}
          </label>
          <p className="text-xs text-[#5B6B64]">
            {personaId
              ? `Queda como responsable en el personal asignado${guardandoPersona ? ' (guardando...)' : ''}.`
              : 'Se guarda al elegirla y queda como responsable en el personal asignado.'}
          </p>
          {avisoPersona && <p className="text-xs text-[#8A1C12]" data-testid="quien-va-persona-error">{avisoPersona}</p>}
          {/* Orden de personal extra: siempre accesible (con equipo propio
              también pueden hacer falta extras, o pedirlos a mano) */}
          {onAbrirSolicitud && (hayOrdenExtra ? (
            <button
              type="button"
              onClick={onAbrirSolicitud}
              className="self-start text-sm font-semibold text-[#11704F] hover:text-[#0B5139] hover:underline"
              data-testid="quien-va-abrir-orden-propio"
            >
              Ver la orden de personal extra ({plural(Number(personasOrdenExtra) || 0, 'persona', 'personas')})
            </button>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              {externosCalculo.total > 0 ? (
                <button type="button" onClick={onAbrirSolicitud} className={botonPrincipal} data-testid="quien-va-solicitar-extras">
                  {externosCalculo.total === 1 ? 'Solicitar 1 extra del evento' : `Solicitar los ${externosCalculo.total} extras del evento`}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onAbrirSolicitud}
                  className="self-start text-sm font-semibold text-[#11704F] hover:text-[#0B5139] hover:underline"
                  data-testid="quien-va-solicitar-extras"
                >
                  Añadir personal extra (opcional)
                </button>
              )}
              {textoDesgloseExternos && (
                <span className="text-xs text-[#5B6B64]" data-testid="quien-va-desglose-extras">{textoDesgloseExternos}</span>
              )}
            </div>
          ))}
          {slotPropio}
        </div>
      )}
      {modo === 'extra' && slotExtra && (
        <div className="rounded-lg border border-[#E1E6E3] px-3 py-2.5 flex flex-col gap-2" data-testid="quien-va-extra">
          {slotExtra}
        </div>
      )}

      {/* Durante el evento: se queda (por defecto) o se va y vuelve a recoger.
          Es de todo el evento: con una jornada elegida solo se recuerda */}
      {aj && (
        <p className="text-xs text-[#5B6B64]" data-testid="quien-va-jornada-recogida">
          {seVa ? 'Recogida más tarde: se va y vuelve a recoger' : 'Se queda durante el evento'} (vale para todas las jornadas; se cambia en la vista Todas).
        </p>
      )}
      {!aj && (
      <div className="rounded-lg border border-[#E1E6E3] px-3 py-2.5 flex flex-col gap-2" data-testid="quien-va-permanencia">
        <label className="inline-flex items-center gap-2 text-sm font-semibold text-[#16221D] cursor-pointer">
          <input
            type="checkbox"
            checked={seVa}
            onChange={(e) => cambiarPermanencia(e.target.checked)}
            className="h-4 w-4 accent-duetto-primary"
            data-testid="quien-va-recogida-mas-tarde"
          />
          Recogida más tarde: se va y vuelve a recoger
        </label>
        <p className="text-xs text-[#5B6B64]">
          {seVa
            ? 'Dos viajes: carga, ida, montaje y vuelta a la nave; después ida a recoger, recogida, vuelta y descarga. La espera no se cuenta y el gasoil es de los dos viajes.'
            : 'Se queda durante todo el evento (lo normal).'}
        </p>
        {seVa && (
          <div className="flex flex-col gap-2 pl-6" data-testid="quien-va-recogida">
            <div className="flex flex-wrap gap-3 text-sm text-[#16221D]">
              <label className="inline-flex items-center gap-1.5 cursor-pointer">
                <input type="radio" name="tipo-recogida" checked={tipoRecogida === 'mismo_dia'}
                  onChange={() => cambiarTipoRecogida('mismo_dia')} className="accent-duetto-primary"
                  data-testid="quien-va-recogida-mismo-dia" />
                Recogida más tarde el mismo día
              </label>
              <label className="inline-flex items-center gap-1.5 cursor-pointer">
                <input type="radio" name="tipo-recogida" checked={tipoRecogida === 'otro_dia'}
                  onChange={() => cambiarTipoRecogida('otro_dia')} className="accent-duetto-primary"
                  data-testid="quien-va-recogida-otro-dia" />
                Recogida otro día
              </label>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-sm text-[#16221D]">
              {tipoRecogida === 'otro_dia' && (
                <>
                  Fecha
                  <input
                    type="date"
                    value={fechaRecogida || ''}
                    min={String(presupuesto?.fecha_evento || '').slice(0, 10) || undefined}
                    onChange={(e) => { setFechaRecogida(e.target.value); limpiar(v); }}
                    className="px-2 py-1.5 min-h-[44px] md:min-h-0 text-base md:text-sm border border-[#CBD3CF] rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                    data-testid="quien-va-fecha-recogida"
                  />
                </>
              )}
              Hora de la recogida
              <input
                type="time"
                value={horaRecogida || (multi || tipoRecogida === 'otro_dia' ? '' : (presupuesto?.hora_fin || ''))}
                onChange={(e) => { setHoraRecogida(e.target.value); limpiar(v); }}
                className={`w-28 ${claseInput}`}
                data-testid="quien-va-hora-recogida"
              />
              {!horaRecogida && tipoRecogida === 'mismo_dia' && (
                <span className="text-xs text-[#5B6B64]">
                  {multi ? 'Por defecto, la hora de fin de cada jornada' : 'Por defecto, la hora de fin del evento'}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-sm text-[#16221D]">
              Quién recoge
              <select
                value={quienRecoge}
                onChange={(e) => { setQuienRecoge(e.target.value); limpiar(v); }}
                className="px-2 py-1.5 min-h-[44px] md:min-h-0 text-base md:text-sm border border-[#CBD3CF] rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary bg-white"
                data-testid="quien-va-quien-recoge"
              >
                {QUIEN_RECOGE.map(q => (
                  <option key={q.clave} value={q.clave}>{q.etiqueta}</option>
                ))}
              </select>
              {quienRecoge === 'otro_evento' && (
                <>
                  <span className="text-xs text-[#5B6B64]">Minutos de recogida (opcional)</span>
                  <input
                    type="number" min="0" max="1440" step="5"
                    value={minutosRecogida}
                    onChange={(e) => { setMinutosRecogida(Math.min(1440, Math.max(0, parseInt(e.target.value, 10) || 0))); limpiar(v); }}
                    className={`w-20 ${claseInput}`}
                    data-testid="quien-va-minutos-recogida"
                  />
                </>
              )}
            </div>
            {quienRecoge === 'otro_evento' && !(Number(minutosRecogida) > 0) && (
              <p className="text-xs text-[#0F5E43] bg-[#E3F3EC] rounded-md px-2 py-1.5" data-testid="quien-va-recogida-sin-coste">
                Sin coste: se recoge aprovechando otro evento.
              </p>
            )}
            {quienRecoge === 'otro_evento' && Number(minutosRecogida) > 0 && (
              <p className="text-xs text-[#5B6B64]">Se imputan {formatoMinutos(minutosRecogida)} a la tarifa del equipo propio; sin segundo viaje.</p>
            )}
            {(quienRecoge === 'propio' || quienRecoge === 'extra') && quienRecoge !== (modo || 'propio') && (
              <p className="text-xs text-[#5B6B64]">
                La recogida (ida a recoger, recogida, vuelta y descarga) la hace otra persona: {quienRecogeTexto.toLowerCase()}, a su tarifa.
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2 text-xs text-[#16221D]" data-testid="quien-va-linea-recogida">
              {lineaRecogida ? (
                <>
                  <span>
                    Línea de venta "{lineaRecogida.nombre}": {lineaRecogida.es_cortesia
                      ? <strong>regalada</strong>
                      : <strong className="tabular-nums">{formatEuros(lineaRecogida.total)} + IVA</strong>}
                    {lineaRecogida.es_cortesia && lineaRecogida.regalo
                      ? ` (por ${lineaRecogida.regalo.usuario || 'alguien'})` : ''}. Se edita como cualquier suplemento.
                  </span>
                  <button
                    type="button"
                    onClick={() => regalarRecogida(lineaRecogida.es_cortesia ? 'quitar_regalo' : 'regalar')}
                    disabled={regalando}
                    className="font-semibold text-[#11704F] hover:underline disabled:opacity-50"
                    data-testid="quien-va-regalar-recogida"
                  >
                    {lineaRecogida.es_cortesia ? 'Volver a cobrarla' : 'Regalar'}
                  </button>
                </>
              ) : (
                <span className="text-[#5B6B64]">Al guardar se añade la línea de venta "Recogida" al presupuesto (editable y regalable).</span>
              )}
            </div>
          </div>
        )}
        {sugerencias.map(texto => (
          <p key={texto} className="text-xs text-[#7A3A06] bg-[#FDF0DC] rounded-md px-2 py-1.5" data-testid="quien-va-sugerencia-recogida">{texto}</p>
        ))}
      </div>
      )}
      {/* Resto del personal propio: Personal asignado (ya no es tarjeta de Logística) */}
      {onAbrirPersonal && (
        <button
          type="button"
          onClick={onAbrirPersonal}
          className="self-start text-sm font-semibold text-[#11704F] hover:text-[#0B5139] hover:underline"
          data-testid="quien-va-mas-personal"
        >
          Más personal propio ({plural(Number(numPersonalAsignado) || 0, 'asignado', 'asignados')})
        </button>
      )}
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Horario del responsable y tramos
// ─────────────────────────────────────────────────────────────────────────────

// Nombres en lenguaje llano (Logística 2)
const nombreLlano = (clave, { destino = '', seVa = false } = {}) => ({
  carga: 'En la nave, carga',
  ida: `Sale hacia ${destino || 'el evento'}`,
  montaje: 'Llega y monta',
  vuelta_montaje: 'Vuelve a la nave',
  evento: 'Evento',
  ida_recogida: 'Sale a recoger',
  desmontaje: seVa ? 'Llega y recoge' : 'Termina y recoge',
  vuelta: 'Vuelta',
  descarga: 'Descarga',
}[clave]);

// Aviso de solape de montaje (Logística 2), intercalado en la tabla
const AvisoSolape = ({ s, nombre, onDecidir, decidiendo }) => {
  const pendiente = !s.decision;
  const quien = nombre || 'quien va';
  return (
    <div
      className={`px-3 py-2 text-sm ${pendiente ? 'bg-[#FDF0DC] text-[#7A3A06]' : 'bg-[#F7F9F8] text-[#33443C]'}`}
      data-testid={pendiente ? 'l2-solape-pendiente' : 'l2-solape-decidido'}
    >
      <p>
        <strong className="tabular-nums">{s.hora_inicio}</strong>{' '}
        <span data-testid="l2-solape-texto">{textoSolape(s)}.</span>
        {pendiente && ` ¿Puede ${quien} con los dos?`}
      </p>
      {pendiente ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => onDecidir(s.clave_decision, 'extra')} disabled={!!decidiendo}
            className={botonPrincipal} data-testid="l2-solape-extra">
            Añadir 1 extra
          </button>
          <button type="button" onClick={() => onDecidir(s.clave_decision, 'ignorado')} disabled={!!decidiendo}
            className={botonSecundario} data-testid="l2-solape-ignorar">
            {nombre ? `Lo hace ${nombre} solo` : 'Lo hace quien va, solo'}
          </button>
        </div>
      ) : (
        <p className="mt-1 flex flex-wrap items-center gap-2 text-xs">
          <span data-testid="l2-solape-decision">
            {s.decision === 'extra'
              ? `Decidido: 1 extra de ${s.hora_inicio} a ${s.hora_fin}.`
              : `Decidido: lo hace ${quien} solo.`}
          </span>
          <button type="button" onClick={() => onDecidir(s.clave_decision, null)} disabled={!!decidiendo}
            className="text-xs font-semibold text-[#11704F] hover:underline disabled:opacity-50" data-testid="l2-solape-cambiar">
            Cambiar
          </button>
        </p>
      )}
    </div>
  );
};

export const HorarioTramos = ({ v, variante = 'l1', nombre = '', destino = '' }) => {
  const {
    multi, raiz, datos, editados, tramos, seVa, tipoRecogida, presupuesto, ajustarExtremo, cambiarTramo,
    restablecerTramo, comparativa, permanencia, modo, tarifas, jornadas,
  } = v;
  const l2 = variante === 'l2';
  const horario = raiz.horario;
  // Horas de reloj de cada tramo (primer bloque y, si hay, el de la recogida)
  const horasTramo = {};
  (horario?.bloques || []).forEach((b, i) => {
    b.tramos.forEach((t) => {
      // En el segundo bloque, desmontaje, vuelta y descarga son los de la recogida
      if (!horasTramo[t.clave] || i > 0) horasTramo[t.clave] = { ...t, fecha: b.fecha };
    });
  });
  const hora = (h, d) => (l2 ? horaRedondeadaConDia(h, d) : horaConDia(h, d));
  const duracion = (m) => (l2 ? duracionRedondeada(m) : formatoMinutos(m));
  const textoBloque = (b) => `${hora(b.inicio, b.dia_inicio)} - ${hora(b.fin, b.dia_fin)} (${duracion(b.minutos)})`;
  const modoTabla = modo || 'propio';
  const avisos = multi ? [...new Set(jornadas.flatMap(x => x.est.avisos))] : raiz.avisos;
  const tramosVisibles = TRAMOS_QUIEN_VA.filter(t => !t.segundoViaje || seVa || editados.includes(t.clave));
  const etiquetaTramo = (t) => (l2 ? nombreLlano(t.clave, { destino, seVa }) || t.etiqueta : t.etiqueta);
  const etiquetaJornada = l2 && nombre ? `Jornada de ${nombre}` : 'Jornada del responsable';

  const filaTramo = (t, h) => {
    const editado = editados.includes(t.clave);
    let ayuda = null;
    if (t.clave === 'ida') {
      ayuda = datos.ida_sin_dato ? 'Sin minutos de Google: escríbelos' : `Google: ${datos.tramos_base?.ida ?? datos.tramos_defecto.ida} min`;
    } else if (t.clave === 'vuelta') {
      ayuda = 'Igual que la ida';
    } else if (t.segundoViaje) {
      ayuda = 'Igual que la ida (segundo viaje)';
    } else if (t.clave === 'evento') {
      if (seVa && tipoRecogida === 'otro_dia') ayuda = 'Recogida otro día: el primer día termina al acabar el montaje o el servicio';
      else if (seVa) ayuda = 'Se va y vuelve: la espera no se cuenta';
      else if (datos.evento_sin_dato) ayuda = 'Sin horario del evento: escríbelo';
      else if (raiz.eventoAmpliado) ayuda = `Ampliado a ${formatoMinutos(raiz.cubiertos)}: cubre el servicio de camarero`;
      else if (datos.por_servicio) ayuda = `Del inicio del primer servicio (${datos.hora_inicio || '?'}) al fin del último (${datos.hora_fin || '?'}); los montajes intermedios se hacen desde dentro`;
      else ayuda = `De ${presupuesto?.hora_inicio || '?'} a ${presupuesto?.hora_fin || '?'}`;
    } else if (datos.por_servicio && t.clave === 'montaje') {
      ayuda = 'Montaje del primer servicio';
    } else if (datos.por_servicio && t.clave === 'desmontaje') {
      ayuda = 'Desmontaje del último servicio';
    } else if (t.clave === 'montaje' || t.clave === 'desmontaje') {
      ayuda = `Formato ${datos.formato_nombre}${datos.formato_detectado ? '' : ' (por defecto)'}`;
    }
    const avisoFalta = (t.clave === 'ida' && datos.ida_sin_dato && !editado)
      || (t.clave === 'evento' && !seVa && datos.evento_sin_dato && !editado);
    const porDefecto = t.clave === 'vuelta' || t.segundoViaje ? tramos.ida : raiz.defecto[t.clave];
    const etiqueta = etiquetaTramo(t);
    return (
      <div key={t.clave} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2" data-testid={`quien-va-tramo-${t.clave}`}>
        <div className="min-w-0">
          <label htmlFor={`quien-va-${t.clave}`} className="text-sm font-semibold text-[#16221D]">{etiqueta}</label>
          {!multi && h && (
            <span className="ml-2 text-sm tabular-nums text-[#16221D]" data-testid={`quien-va-horas-${t.clave}`}>
              {hora(h.inicio, h.dia_inicio)} - {hora(h.fin, h.dia_fin)}
              {!l2 && horario?.otroDia && h.fecha ? ` (${fechaCorta(h.fecha)})` : ''}
              <span className="text-xs text-[#5B6B64]"> ({formatoMinutos(h.minutos)})</span>
            </span>
          )}
          {ayuda && (
            <span className={`block text-xs ${avisoFalta ? 'text-[#92400E]' : 'text-[#5B6B64]'}`}>
              {editado ? `Editado a mano · por defecto ${formatoMinutos(porDefecto)}` : ayuda}
            </span>
          )}
          {!ayuda && editado && (
            <span className="block text-xs text-[#5B6B64]">Editado a mano · por defecto {formatoMinutos(porDefecto)}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {editado && (
            <button
              type="button"
              onClick={() => restablecerTramo(t.clave)}
              className="text-xs font-semibold text-[#11704F] hover:underline"
              title="Volver al valor por defecto"
            >
              Restablecer
            </button>
          )}
          <input
            id={`quien-va-${t.clave}`}
            type="number"
            min="0"
            max="1440"
            step="5"
            value={tramos[t.clave]}
            onChange={(e) => cambiarTramo(t.clave, e.target.value)}
            className={`w-20 ${claseInput}`}
            aria-label={`${etiqueta} (minutos)`}
          />
          <span className="text-xs text-[#5B6B64] w-7">min</span>
        </div>
      </div>
    );
  };

  // Logística 2: filas por bloque (el segundo viaje separado por "Recogida"),
  // con el solape de montaje intercalado donde empieza
  const filasL2 = () => {
    const porClave = Object.fromEntries(TRAMOS_QUIEN_VA.map(t => [t.clave, t]));
    const bloques = horario?.bloques || [];
    if (multi || bloques.length === 0) return tramosVisibles.map(t => filaTramo(t, null));
    const solapes = [...(raiz.solapes || [])];
    const colocados = new Set();
    const salida = [];
    bloques.forEach((b, i) => {
      if (i > 0) {
        salida.push(
          <div key={`recogida-${i}`} className="px-3 py-1.5 bg-[#F7F9F8] text-xs font-bold tracking-wider uppercase text-[#5B6B64]" data-testid="l2-tramos-recogida">
            Recogida{b.fecha && (horario.otroDia || b.fecha !== bloques[0].fecha) ? ` (${fechaDiaMes(b.fecha)})` : ''}
          </div>
        );
      }
      b.tramos.forEach((tr) => {
        const t = porClave[tr.clave];
        if (!t) return;
        if (t.segundoViaje && !seVa && !editados.includes(t.clave) && !(Number(tr.minutos) > 0)) return;
        salida.push(filaTramo(t, tr));
        if (i === 0) {
          const ini = minutosDeHora(tr.inicio) + (Number(tr.dia_inicio) || 0) * 1440;
          const fin = minutosDeHora(tr.fin) + (Number(tr.dia_fin) || 0) * 1440;
          solapes.forEach((s) => {
            const m = minutosDeHora(s.hora_inicio);
            if (colocados.has(s.clave_decision) || m === null) return;
            if (m >= ini && m < Math.max(fin, ini + 1)) {
              colocados.add(s.clave_decision);
              salida.push(<AvisoSolape key={`solape-${s.clave_decision}`} s={s} nombre={nombre}
                onDecidir={v.decidirSolape} decidiendo={v.decidiendoSolape} />);
            }
          });
        }
      });
    });
    solapes.filter(s => !colocados.has(s.clave_decision)).forEach((s) => {
      salida.push(<AvisoSolape key={`solape-${s.clave_decision}`} s={s} nombre={nombre}
        onDecidir={v.decidirSolape} decidiendo={v.decidiendoSolape} />);
    });
    return salida;
  };

  return (
    <section className="flex flex-col gap-3 min-w-0" data-testid="quien-va-horario-tramos">
      {!l2 && <h3 className={claseTitulo}>Horario del responsable y tramos</h3>}

      {l2 && !horario && !multi && (
        <p className="text-sm text-[#7A3A06]">Falta la hora del evento para calcular el horario.</p>
      )}

      {!multi && horario && (
        <div className="flex flex-col gap-1.5" data-testid="quien-va-jornada-responsable">
          {(horario.bloques || []).map((b, i) => (
            <p key={`bloque-${i}`} className="text-sm font-semibold text-[#16221D]">
              {horario.otroDia
                ? `${i === 0 ? 'Día del evento' : 'Día de la recogida'}${b.fecha ? ` (${fechaCorta(b.fecha)})` : ''}: ${textoBloque(b)}`
                : `${(horario.bloques || []).length > 1 ? (i === 0 ? 'Montaje' : 'Recogida') : etiquetaJornada}: ${textoBloque(b)}`}
            </p>
          ))}
          {horario.dosViajes && !horario.otroDia && (
            <p className="text-xs text-[#5B6B64]">{etiquetaJornada}: {duracion(horario.totalMinutos)} de trabajo (la espera no se cuenta).</p>
          )}
          {/* Hora por servicio: línea del día con los servicios intercalados */}
          {!l2 && raiz.lineaDia && (
            <div className="rounded-lg border border-[#E1E6E3] px-3 py-2" data-testid="quien-va-linea-dia">
              <span className="block text-xs font-bold text-[#16221D] mb-1">Línea del día</span>
              <ol className="flex flex-col gap-0.5">
                {lineaDelDia(horario, raiz.lineaDia).map(seg => (
                  <li
                    key={seg.clave}
                    className={`text-xs tabular-nums ${seg.tipo === 'servicio' ? 'font-bold text-[#0F5E43]'
                      : (seg.solape ? 'text-[#8A1C12]' : 'text-[#33443C]')}`}
                  >
                    {seg.texto}
                  </li>
                ))}
              </ol>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3 text-xs text-[#5B6B64]">
            <label className="inline-flex items-center gap-1.5">
              Llegada a la nave
              <input
                type="time"
                value={horario.llegadaNave || ''}
                onChange={(e) => ajustarExtremo('carga', e.target.value)}
                className={`w-24 ${claseInput}`}
                data-testid="quien-va-hora-llegada-nave"
              />
            </label>
            <label className="inline-flex items-center gap-1.5">
              Fin de la descarga
              <input
                type="time"
                value={horario.finDescarga || ''}
                onChange={(e) => ajustarExtremo('descarga', e.target.value)}
                className={`w-24 ${claseInput}`}
                data-testid="quien-va-hora-fin-descarga"
              />
            </label>
            <span>Al cambiarlas se ajusta la carga o la descarga.</span>
          </div>
        </div>
      )}

      <div className="border border-[#E1E6E3] rounded-lg divide-y divide-[#EEF1EF]" data-testid="quien-va-tramos">
        {l2 ? filasL2() : tramosVisibles.map(t => filaTramo(t, horasTramo[t.clave]))}
      </div>
      {l2 && multi && jornadas.some(x => (x.est.solapes || []).length > 0) && (
        <div className="flex flex-col gap-2">
          {jornadas.filter(x => (x.est.solapes || []).length > 0).map(({ j, est }) => (
            <div key={j.id} className="border border-[#F4D9AE] rounded-lg overflow-hidden">
              <span className="block px-3 pt-2 text-xs font-semibold text-[#5B6B64]">{j.etiqueta}</span>
              {est.solapes.map(s => (
                <AvisoSolape key={s.clave_decision} s={s} nombre={nombre} onDecidir={v.decidirSolape} decidiendo={v.decidiendoSolape} />
              ))}
            </div>
          ))}
        </div>
      )}
      {(multi || v.ambitoJornada) && (
        <p className="text-xs text-[#5B6B64]" data-testid="quien-va-tramos-multievento">
          Multievento: cada jornada se calcula con su horario, sus menús y sus minutos de ruta. Un tramo editado a mano se aplica a todas las jornadas.
        </p>
      )}
      {!l2 && !multi && horario && (
        <p className="text-sm text-[#5B6B64]" data-testid="quien-va-horario">
          {horario.dosViajes
            ? `En la nave a las ${horario.llegadaNave}; vuelve a las ${horario.finPrimerViaje}. Sale a recoger${horario.otroDia && horario.fechaRecogida ? ` el ${fechaCorta(horario.fechaRecogida)}` : ''} a las ${horario.salidaRecogida} (recogida a las ${horario.horaRecogida}); termina la descarga a las ${horario.finDescarga}`
            : `En la nave a las ${horario.llegadaNave}; termina la descarga a las ${horario.finDescarga}`}
        </p>
      )}
      {avisos.map(texto => (
        <p key={texto} className="text-xs text-[#8A1C12] bg-[#FDE8E6] rounded-md px-2 py-1.5" data-testid="quien-va-aviso-tramos">{texto}</p>
      ))}

      {/* Ayuda para decidir (solo con "Recogida más tarde"): se queda / se va y
          vuelve con el modo elegido (sin elegir, equipo propio) */}
      {!l2 && seVa && (
        <div className="overflow-x-auto" data-testid="quien-va-comparativa">
          <table className="w-full text-xs border border-[#E1E6E3] rounded-lg">
            <thead>
              <tr className="bg-[#F7F9F8] text-[#5B6B64]">
                <th className="px-2 py-1.5 text-left font-semibold">Responsable{multi ? ' (todas las jornadas)' : ''}</th>
                <th className="px-2 py-1.5 text-right font-semibold">
                  {modoTabla === 'extra' ? 'Extra' : 'Equipo propio'} ({formatEuros(tarifas[modoTabla])}/h){modo ? '' : ' · sin elegir: equipo propio'}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EEF1EF]">
              {[['se_queda', 'Se queda'], ['se_va', tipoRecogida === 'otro_dia' ? 'Recogida otro día' : 'Se va y vuelve']].map(([fila, etiqueta]) => {
                const c = comparativa[fila];
                const elegida = permanencia === fila;
                return (
                  <tr key={fila}>
                    <td className="px-2 py-1.5 text-[#16221D]">{etiqueta}</td>
                    <td className={`px-2 py-1.5 text-right tabular-nums ${elegida ? 'bg-[#E3F3EC] font-bold text-[#0F5E43]' : ''}`}
                      data-testid={`quien-va-comparativa-${fila}-${modoTabla}`}>
                      {formatHoras(c.horas)} h · {formatEuros(c[modoTabla])}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="mt-1 text-xs md:text-[11px] text-[#8A9892]" data-testid="quien-va-comparativa-diferencia">
            {(() => {
              const dif = r2(comparativa.se_va[modoTabla] - comparativa.se_queda[modoTabla]);
              if (Math.abs(dif) < 0.005) return 'Cuesta lo mismo que quedarse.';
              return dif < 0
                ? `Recoger más tarde ahorra ${formatEuros(-dif)} frente a quedarse.`
                : `Recoger más tarde cuesta ${formatEuros(dif)} más que quedarse.`;
            })()}
            {' '}Incluye el gasoil de sus viajes ({formatEuros(comparativa.se_queda.gasoil)} si se queda, {formatEuros(comparativa.se_va.gasoil)} con la recogida). Resaltada, la opción elegida: es la que entra en el coste y el margen.
          </p>
        </div>
      )}
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// b) Personal del menú según ratios
// ─────────────────────────────────────────────────────────────────────────────

const entero = (x) => {
  const n = parseInt(x, 10);
  return Number.isNaN(n) ? 0 : n;
};

// Número con - y + (36 px en escritorio, 44 px en el móvil) que también se
// escribe a mano. `onCambiar` recibe un número (botones) o el texto escrito
const claseBotonPaso = 'inline-flex items-center justify-center w-11 h-11 sm:w-9 sm:h-9 rounded-lg border border-[#CBD3CF] bg-white text-[#16221D] hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed';
const claseNumeroPaso = 'w-14 h-11 sm:h-9 px-1 text-base sm:text-sm text-center font-semibold border border-[#CBD3CF] rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary tabular-nums';

const Contador = ({ valor, onCambiar, min = 0, max = 500, paso = 1, decimales = false, etiqueta = '', testid }) => {
  const n = Number(valor) || 0;
  const fijar = (x) => {
    const redondeado = decimales ? Math.round(x * 100) / 100 : Math.round(x);
    onCambiar(Math.min(max, Math.max(min, redondeado)));
  };
  return (
    <span className="inline-flex items-center gap-1" data-testid={testid}>
      <button type="button" onClick={() => fijar(n - paso)} disabled={n <= min} className={claseBotonPaso}
        aria-label={`Quitar ${etiqueta}`} title="Uno menos" data-testid={testid ? `${testid}-menos` : undefined}>
        <Minus className="w-4 h-4" strokeWidth={2.5} />
      </button>
      <input
        type="number"
        inputMode={decimales ? 'decimal' : 'numeric'}
        min={min}
        max={max}
        step={paso}
        value={valor ?? ''}
        onChange={(e) => onCambiar(e.target.value)}
        className={claseNumeroPaso}
        aria-label={etiqueta}
        data-testid={testid ? `${testid}-valor` : undefined}
      />
      <button type="button" onClick={() => fijar(n + paso)} disabled={n >= max} className={claseBotonPaso}
        aria-label={`Añadir ${etiqueta}`} title="Uno más" data-testid={testid ? `${testid}-mas` : undefined}>
        <Plus className="w-4 h-4" strokeWidth={2.5} />
      </button>
    </span>
  );
};

// Tanda nueva válida: de `desde` durante `minutos` con `camareros`. Si así
// cruzara la medianoche sin ser de noche, se queda en 4 h o, como último
// recurso, de 12:00 a 16:00 (misma regla que las horas de servicio)
const tandaNueva = (desde, minutos, camareros) => {
  const ini = normalizarHoraEscrita(desde) || '12:00';
  const base = minutosDeHora(ini);
  const candidatas = [Math.max(30, minutos || 240), 240];
  for (let i = 0; i < candidatas.length; i += 1) {
    const fin = horaTexto(base + candidatas[i]);
    if (!errorFranja(ini, fin)) return { camareros, hora_inicio: ini, hora_fin: fin };
  }
  return { camareros, hora_inicio: '12:00', hora_fin: '16:00' };
};

const claseHoraTanda = 'h-11 sm:h-9 w-[6.5rem] px-2 border border-[#CBD3CF] rounded-lg text-sm text-[#16221D] bg-white tabular-nums focus:outline-none focus:ring-2 focus:ring-duetto-primary';

// Una tanda: "[-] 2 [+] camareros de [11:00] a [15:00] (4 h) Quitar". Las
// horas, con el mismo control que las horas de servicio: una pareja imposible
// (fin antes que inicio salvo de noche) se enseña en rojo y no se guarda
const TandaFila = ({ tanda, nombre, onCambiar, onQuitar, testid }) => {
  const [borrador, setBorrador] = useState(null);
  const ini = borrador ? borrador.ini : (tanda.hora_inicio || '');
  const fin = borrador ? borrador.fin : (tanda.hora_fin || '');
  const errorDe = (a, b) => ((!a || !b) ? 'Faltan las horas' : errorFranja(a, b));
  const error = errorDe(ini, fin);
  const cambiarHora = (campo, valor) => {
    const nuevo = { ini, fin, [campo]: normalizarHoraEscrita(valor) || valor };
    if (errorDe(nuevo.ini, nuevo.fin)) {
      setBorrador(nuevo);
      return;
    }
    setBorrador(null);
    onCambiar({ ...tanda, hora_inicio: nuevo.ini, hora_fin: nuevo.fin });
  };
  const camareros = entero(tanda.camareros);
  const cambiarCamareros = (x) => {
    const limpio = String(x).replace(/[^\d]/g, '');
    onCambiar({ ...tanda, camareros: Math.min(500, Math.max(1, parseInt(limpio, 10) || 1)) });
  };
  return (
    <li className="flex flex-wrap items-center gap-2 text-sm text-[#16221D]" data-testid={testid}>
      <Contador valor={tanda.camareros} min={1} max={500} etiqueta={`camareros de la tanda de ${nombre}`}
        onCambiar={cambiarCamareros} testid={testid ? `${testid}-camareros` : undefined} />
      <span>{camareros === 1 ? 'camarero' : 'camareros'} de</span>
      <input type="time" value={ini} onChange={(e) => cambiarHora('ini', e.target.value)} className={claseHoraTanda}
        aria-label="Hora de entrada de la tanda" title="Hora de entrada" data-testid={testid ? `${testid}-inicio` : undefined} />
      <span>a</span>
      <input type="time" value={fin} onChange={(e) => cambiarHora('fin', e.target.value)} className={claseHoraTanda}
        aria-label="Hora de salida de la tanda" title="Hora de salida" data-testid={testid ? `${testid}-fin` : undefined} />
      {error ? (
        <span className={CLASE_ERROR_FRANJA} data-testid={testid ? `${testid}-error` : undefined}>
          {error}{borrador ? ': no se guarda' : ''}
        </span>
      ) : (
        <span className="text-xs text-[#5B6B64] tabular-nums">{formatoMinutos(minutosTanda({ hora_inicio: ini, hora_fin: fin }))}</span>
      )}
      <button type="button" onClick={onQuitar} className="text-xs font-semibold text-[#8A1C12] hover:underline"
        data-testid={testid ? `${testid}-quitar` : undefined}>
        Quitar
      </button>
    </li>
  );
};

// ¿La fila lleva personal? (Logística 2) Camareros o extras (ratio o a mano),
// líneas de servicio de camarero, solapes y todo lo editado a mano
export const filaConPersonal = (f) => (
  f?.origen === 'solape_montaje' || !!f?.por_horas || (f?.editados || []).length > 0
  || (parseInt(f?.camareros, 10) || 0) + (parseInt(f?.extras, 10) || 0) > 0
);

export const PersonalMenu = ({ v, variante = 'l1' }) => {
  const {
    tarifas, multi, jornadas, raiz, calculo, cambiarCubre, restablecerMenu, cambiarMenu, decidirSolape, decidiendoSolape,
    cambiarTandas, volverAPropuesto,
  } = v;
  const l2 = variante === 'l2';
  const [verTodos, setVerTodos] = useState(false);
  // La tarjeta se ve sin cambiar de ruta (subpestaña de Logística): avisa a la
  // guía de novedades para que mire si toca enseñar la de camareros y tandas
  useEffect(() => {
    const t = setTimeout(revisarNovedades, 300);
    return () => clearTimeout(t);
  }, []);
  // Guardado de lo que se cambia aquí (la cola del autoguardado de la ficha)
  let estadoGuardado = null;
  if (v.aviso && v.aviso.tipo === 'error') estadoGuardado = { clase: 'text-[#8A1C12]', texto: v.aviso.texto };
  else if (v.hayCambios && v.faltaParaGuardar) estadoGuardado = { clase: 'text-[#92400E]', texto: v.faltaParaGuardar };
  else if (v.guardando || v.hayCambios) estadoGuardado = { clase: 'text-[#5B6B64]', texto: 'Guardando...' };
  else if (v.datos) estadoGuardado = { clase: 'text-[#11704F]', texto: 'Guardado' };

  // Solape de montaje sin decidir: aviso con los dos botones (sin inputs)
  const filaSolapePendiente = (f) => (
    <div key={f.clave} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 bg-[#FDF0DC]" data-testid={`quien-va-menu-${f.clave}`}>
      <div className="min-w-0">
        <span className="text-sm font-semibold text-[#16221D]">Solape de montaje</span>
        <span className="block text-xs text-[#7A3A06]" data-testid="quien-va-solape-texto">{f.texto}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => decidirSolape(f.clave_decision, 'extra')} disabled={!!decidiendoSolape}
          className={botonPrincipal} data-testid="quien-va-solape-extra">
          Añadir 1 extra
        </button>
        <button type="button" onClick={() => decidirSolape(f.clave_decision, 'ignorado')} disabled={!!decidiendoSolape}
          className={botonSecundario} data-testid="quien-va-solape-ignorar">
          Ignorar
        </button>
      </div>
    </div>
  );

  // Solapes ignorados de un grupo: línea discreta para volver a mostrarlos
  const solapesIgnorados = (est) => (est?.solapes || []).filter(s => s.decision === 'ignorado').map(s => (
    <p key={s.clave_decision} className="text-xs text-[#8A9892]" data-testid="quien-va-solape-ignorado">
      Solape de montaje ignorado ({s.nombre}, {s.hora_inicio} a {s.hora_fin}){' '}
      <button type="button" onClick={() => decidirSolape(s.clave_decision, null)} disabled={!!decidiendoSolape}
        className="font-semibold text-[#11704F] hover:underline disabled:opacity-50" data-testid="quien-va-solape-restablecer">
        (restablecer)
      </button>
    </p>
  ));

  // Primera fila con personal y primer botón de tandas: zonas de la guía de novedades
  let guiaCamareros = false;
  let guiaTandas = false;

  const filaPersonal = (f, horaRef = '') => {
    if (f.origen === 'solape_montaje' && f.solape_pendiente) return filaSolapePendiente(f);
    const cubre = !!f.cubre_responsable;
    const d = f.defecto || {};
    const esSolapeFila = f.origen === 'solape_montaje';
    const tandas = Array.isArray(f.tandas) ? f.tandas : [];
    const camareros = entero(f.camareros);
    let ayuda = '';
    if (esSolapeFila) {
      ayuda = `Solape de montaje: 1 extra de ${f.hora_inicio} a ${f.hora_fin} mientras se sirve el servicio anterior`;
    } else if (f.por_horas && f.por_horas.por_unidad === 'camarero') {
      ayuda = `servicio de camarero, 1 camarero por unidad × ${formatHoras(f.por_horas.horas_por_unidad)} h`;
    } else if (f.por_horas) {
      ayuda = `servicio por horas, ${textoCantidadFila(f)} × ${formatHoras(f.por_horas.horas_por_unidad)} h`;
    } else if ((d.camareros || 0) + (d.extras || 0) > 0) {
      const ratios = [];
      if (f.ratio_camareros > 0) ratios.push(`1 camarero por cada ${f.ratio_camareros}`);
      if (f.ratio_extras > 0) ratios.push(`1 extra por cada ${f.ratio_extras} (desde ${f.ratio_extras} pax)`);
      if (f.origen_extras === 'presupuesto') ratios.push('extras del presupuesto');
      ayuda = ratios.join(' · ');
    }
    const propuesto = (d.camareros || 0) + (d.extras || 0) === 0
      ? 'sin camareros (este menú no los lleva)'
      : [plural(d.camareros || 0, 'camarero', 'camareros'), (d.extras || 0) > 0 ? plural(d.extras, 'extra', 'extras') : null]
        .filter(Boolean).join(' + ') + ` · ${formatHoras(d.horas)} h`;
    const difiere = tandas.length > 0 || camareros !== entero(d.camareros) || entero(f.extras) !== entero(d.extras)
      || r2(f.horas) !== r2(d.horas);
    const puedeTandas = !esSolapeFila && typeof cambiarTandas === 'function';
    const propioJornada = !!(v.ambitoJornada && v.menuPropioJornada && v.menuPropioJornada[f.clave]);
    const marcarCamareros = !guiaCamareros && filaConPersonal(f);
    if (marcarCamareros) guiaCamareros = true;
    const marcarTandas = puedeTandas && !guiaTandas && tandas.length === 0 && filaConPersonal(f);
    if (marcarTandas) guiaTandas = true;

    // Tandas: la primera con los camareros y las horas de ahora; las nuevas
    // empiezan cuando acaba la última (o a la hora del servicio) y duran 4 h
    const horaServicio = normalizarHoraEscrita(f.hora_inicio_servicio) || normalizarHoraEscrita(horaRef) || '12:00';
    const activarTandas = () => cambiarTandas(f.clave, [
      tandaNueva(horaServicio, Math.round((Number(f.horas) || 4) * 60), Math.max(1, camareros)),
    ]);
    const anadirTanda = () => {
      const ultima = tandas[tandas.length - 1];
      const desde = (ultima && normalizarHoraEscrita(ultima.hora_fin)) || horaServicio;
      cambiarTandas(f.clave, [...tandas, tandaNueva(desde, 240, 1)]);
    };
    const cambiarTanda = (i, tanda) => cambiarTandas(f.clave, tandas.map((t, j) => (j === i ? tanda : t)));
    const quitarTanda = (i) => cambiarTandas(f.clave, tandas.filter((_, j) => j !== i));

    return (
      <div key={f.clave} className="flex flex-col gap-2 px-3 py-3" data-testid={`quien-va-menu-${f.clave}`}
        data-guia={marcarCamareros ? 'quien-va-camareros' : undefined}>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="text-sm font-semibold text-[#16221D] min-w-0">{f.nombre} · {textoCantidadFila(f)}</span>
          <span className={`text-sm font-semibold tabular-nums ${cubre ? 'text-[#8A9892]' : 'text-[#16221D]'}`}>
            {formatEuros(horasPersonaFila(f) * (Number(tarifas.extra) || 0))}
          </span>
        </div>
        <p className="text-xs text-[#5B6B64]" data-testid={`quien-va-propuesto-${f.clave}`}>
          {esSolapeFila ? ayuda : <>Propuesto: <strong className="text-[#33443C]">{propuesto}</strong>{ayuda ? ` · ${ayuda}` : ''}</>}
        </p>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-[#16221D]">
          {tandas.length === 0 ? (
            <span className="inline-flex items-center gap-2">
              <span className="font-semibold">Van</span>
              <Contador valor={f.camareros} max={500} etiqueta={`camareros de ${f.nombre}`}
                onCambiar={(x) => cambiarMenu(f.clave, 'camareros', x)} testid={`quien-va-camareros-${f.clave}`} />
              <span>{camareros === 1 ? 'camarero' : 'camareros'}</span>
            </span>
          ) : (
            <span data-testid={`quien-va-camareros-tandas-${f.clave}`}>
              <span className="font-semibold">Van</span> {plural(camareros, 'camarero', 'camareros')} en {plural(tandas.length, 'tanda', 'tandas')}
            </span>
          )}
          <span className="inline-flex items-center gap-2">
            <span className="font-semibold">Extras</span>
            <Contador valor={f.extras} max={500} etiqueta={`extras de ${f.nombre}`}
              onCambiar={(x) => cambiarMenu(f.clave, 'extras', x)} testid={`quien-va-extras-${f.clave}`} />
          </span>
          {tandas.length === 0 && (
            <span className="inline-flex items-center gap-2">
              <span className="font-semibold">Horas</span>
              <Contador valor={f.horas} max={24} paso={0.5} decimales etiqueta={`horas de ${f.nombre}`}
                onCambiar={(x) => cambiarMenu(f.clave, 'horas', x)} testid={`quien-va-horas-${f.clave}`} />
            </span>
          )}
        </div>
        {tandas.length > 0 && (
          <div className="flex flex-col gap-2 rounded-lg bg-[#F7F9F8] px-3 py-2" data-testid={`quien-va-tandas-${f.clave}`}>
            <ul className="flex flex-col gap-2">
              {tandas.map((t, i) => (
                <TandaFila
                  key={`${f.clave}-${i}-${tandas.length}`}
                  tanda={t}
                  nombre={f.nombre}
                  onCambiar={(nueva) => cambiarTanda(i, nueva)}
                  onQuitar={() => quitarTanda(i)}
                  testid={`quien-va-tanda-${f.clave}-${i}`}
                />
              ))}
            </ul>
            <div className="flex flex-wrap items-center gap-3 text-xs">
              <button type="button" onClick={anadirTanda} disabled={tandas.length >= MAX_TANDAS}
                className="font-semibold text-[#11704F] hover:underline disabled:opacity-50 disabled:no-underline"
                data-testid={`quien-va-tanda-anadir-${f.clave}`}>
                + Añadir tanda
              </button>
              <button type="button" onClick={() => cambiarTandas(f.clave, null)}
                className="font-semibold text-[#5B6B64] hover:underline" data-testid={`quien-va-tandas-quitar-${f.clave}`}>
                Quitar todas (todos a la misma hora)
              </button>
            </div>
            {entero(f.extras) > 0 && (
              <p className="text-xs text-[#5B6B64]">
                Cada camarero cuenta las horas de su tanda; los extras cuentan {formatHoras(f.horas)} h (la media de las tandas).
              </p>
            )}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
          <label className="inline-flex items-center gap-1.5 text-[#16221D] cursor-pointer">
            <input
              type="checkbox"
              checked={cubre}
              onChange={(e) => cambiarCubre(f.clave, e.target.checked)}
              className="h-4 w-4 accent-duetto-primary"
              data-testid={`quien-va-cubre-${f.clave}`}
            />
            Lo cubre la persona que va al evento
          </label>
          {puedeTandas && tandas.length === 0 && (
            <button type="button" onClick={activarTandas}
              className="h-9 px-3 rounded-lg border border-[#CBD3CF] bg-white font-semibold text-[#16221D] hover:bg-gray-50"
              data-guia={marcarTandas ? 'quien-va-tandas' : undefined} data-testid={`quien-va-tandas-activar-${f.clave}`}
              title="Unos camareros a una hora y otros a otra">
              Horas distintas
            </button>
          )}
          {difiere && typeof volverAPropuesto === 'function' && (
            <button type="button" onClick={() => volverAPropuesto(f.clave, d)}
              className="font-semibold text-[#11704F] hover:underline" data-testid={`quien-va-propuesto-volver-${f.clave}`}>
              Volver a lo propuesto
            </button>
          )}
          {propioJornada && (
            <button type="button" onClick={() => restablecerMenu(f.clave)}
              className="font-semibold text-[#11704F] hover:underline" title="Quitar lo propio de esta jornada"
              data-testid={`quien-va-usar-evento-${f.clave}`}>
              Usar lo del evento
            </button>
          )}
        </div>
      </div>
    );
  };

  // Logística 2: solo las filas con personal; el resto, en "Ver todos los menús"
  // `horaRef`: hora de inicio del evento o de la jornada (la primera tanda
  // empieza a la hora del servicio y, si no la tiene, a esta)
  const listaL2 = (filas, horaRef) => {
    const con = filas.filter(filaConPersonal);
    const sin = filas.filter(f => !filaConPersonal(f));
    return (
      <>
        {con.length === 0 ? (
          <p className="text-sm text-[#5B6B64]" data-testid="l2-personal-ninguno">Ningún menú lleva camareros ni extras</p>
        ) : (
          <div className="border border-[#E1E6E3] rounded-lg divide-y divide-[#EEF1EF]">
            {con.map(f => filaPersonal(f, horaRef))}
          </div>
        )}
        {sin.length > 0 && verTodos && (
          <div className="border border-dashed border-[#CBD3CF] rounded-lg divide-y divide-[#EEF1EF]" data-testid="l2-personal-sin">
            {sin.map(f => filaPersonal(f, horaRef))}
          </div>
        )}
      </>
    );
  };
  const totalSin = (multi ? jornadas.flatMap(x => x.est.filas) : raiz.filas).filter(f => !filaConPersonal(f)).length;
  const lista = (filas, horaRef = '') => (l2 ? listaL2(filas, horaRef) : (
    <div className="border border-[#E1E6E3] rounded-lg divide-y divide-[#EEF1EF]">
      {filas.map(f => filaPersonal(f, horaRef))}
    </div>
  ));

  const menu = calculo.personalMenu;
  return (
    <section className="flex flex-col gap-3 min-w-0" data-testid="quien-va-personal-menu">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className={claseTitulo}>Personal del menú (según ratios)</h3>
        <span className="flex flex-wrap items-baseline gap-3 text-xs">
          {estadoGuardado && (
            <span className={`font-semibold ${estadoGuardado.clase}`} role="status" aria-live="polite"
              data-testid="quien-va-personal-guardado">
              {estadoGuardado.texto}
            </span>
          )}
          <span className="text-[#5B6B64]">Siempre externo, a {formatEuros(tarifas.extra)}/h</span>
        </span>
      </div>
      <p className="text-xs text-[#5B6B64]">
        Personal que va además de la persona que monta el evento. Propuesto es lo que sale del ratio de cada menú; en Van pones los que irán de verdad. Si unos van a una hora y otros a otra, usa Horas distintas.
        {multi ? ' Lo editado en una línea vale para la misma línea (posición y menú) en todas las jornadas.' : ''}
        {v.ambitoJornada ? ` Lo que cambies aquí vale solo para la ${v.ambitoJornada.etiqueta}; lo demás se toma del evento.` : ''}
      </p>
      {multi ? (
        jornadas.map(({ j, est }) => (
          <div key={j.id} className="flex flex-col gap-1" data-testid={`quien-va-jornada-${j.id}`}>
            <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              {j.etiqueta} · {j.hora_inicio || '?'} a {j.hora_fin || '?'}
            </div>
            {est.filas.length === 0 ? (
              <p className="text-sm text-[#5B6B64]">La jornada no tiene menús.</p>
            ) : lista(est.filas, j.hora_inicio)}
            {solapesIgnorados(est)}
          </div>
        ))
      ) : raiz.filas.length === 0 ? (
        <p className="text-sm text-[#5B6B64]">El presupuesto no tiene menús.</p>
      ) : lista(raiz.filas, v.datos?.hora_inicio || v.presupuesto?.hora_inicio || '')}
      {!multi && solapesIgnorados(raiz)}
      {l2 && totalSin > 0 && (
        <button type="button" onClick={() => setVerTodos(x => !x)} aria-expanded={verTodos}
          className="self-start text-xs font-semibold text-[#11704F] hover:underline" data-testid="l2-personal-ver-todos">
          {verTodos ? 'Ocultar los menús sin personal' : `Ver todos los menús (${totalSin} sin personal)`}
        </button>
      )}
      <p className="text-sm font-semibold text-[#16221D]" data-testid="quien-va-total-menu">
        {menu.personas === 0
          ? 'Sin personal del menú.'
          : `${plural(menu.personas, multi ? 'persona-jornada' : 'persona', multi ? 'personas-jornada' : 'personas')} · ${formatHoras(menu.horasPersona)} h · ${formatEuros(menu.coste)}`}
      </p>
    </section>
  );
};
